import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { API } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { HardHat, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";

const COLORS = {
  design: "bg-slate-100 text-slate-800 border-slate-200",
  cutting: "bg-sky-100 text-sky-800 border-sky-200",
  machining: "bg-indigo-100 text-indigo-800 border-indigo-200",
  assembly: "bg-amber-100 text-amber-800 border-amber-200",
  glazing: "bg-cyan-100 text-cyan-800 border-cyan-200",
  qc: "bg-purple-100 text-purple-800 border-purple-200",
  dispatch: "bg-orange-100 text-orange-800 border-orange-200",
  installed: "bg-emerald-100 text-emerald-800 border-emerald-200",
};

export default function WOScan() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const t = params.get("t") || "";
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setErr("");
    try {
      const r = await fetch(`${API}/public/wo/${id}?t=${encodeURIComponent(t)}`);
      if (!r.ok) throw new Error("Invalid QR link or expired token");
      const j = await r.json();
      setData(j); setNotes(j.wo.notes || "");
    } catch (e) { setErr(e.message); }
  };
  useEffect(() => { load(); }, [id, t]);

  const update = async (status) => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/public/wo/${id}/status?t=${encodeURIComponent(t)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status, notes }),
      });
      if (!r.ok) throw new Error("Update failed");
      toast.success(`Status updated to ${status}`);
      await load();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background p-4 sm:p-6 flex justify-center">
      <div className="w-full max-w-lg">
        <div className="flex items-center gap-2 mb-4">
          <div className="h-9 w-9 rounded-md bg-primary flex items-center justify-center">
            <HardHat className="h-5 w-5 text-primary-foreground" />
          </div>
          <div>
            <div className="font-display font-bold text-sm">Artisan Guild Infra</div>
            <div className="text-[11px] text-muted-foreground font-mono uppercase tracking-widest">YKK Iwin · Shop-floor scan</div>
          </div>
        </div>

        {err && (
          <Card className="shadow-none border-destructive/40 bg-red-50">
            <CardContent className="p-4 text-sm text-destructive">{err}</CardContent>
          </Card>
        )}

        {data && (
          <>
            <Card className="shadow-none">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono">Work Order</div>
                    <CardTitle className="font-display text-2xl font-bold" data-testid="scan-wo-no">{data.wo.wo_no}</CardTitle>
                  </div>
                  <Badge className={COLORS[data.wo.status] || ""} data-testid="scan-current-status">{data.wo.status}</Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-0 space-y-1.5 text-sm">
                <Row label="System" value={data.wo.system} />
                <Row label="Unit type" value={data.wo.unit_type} />
                <Row label="Quantity" value={data.wo.quantity} />
                <Row label="Size (mm)" value={data.wo.size_mm} />
                <Row label="Glass" value={data.wo.glass_spec} />
                <Row label="Finish" value={data.wo.finish} />
                <Row label="Project" value={`${data.project.name} (${data.project.code})`} />
                <Row label="Target dispatch" value={data.wo.target_dispatch} />
              </CardContent>
            </Card>

            <Card className="shadow-none mt-4">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-display">Update status</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <Textarea
                  data-testid="scan-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Optional note for this stage (e.g. blocker, batch #, QC issue)"
                  rows={2}
                  className="mb-3"
                />
                <div className="grid grid-cols-2 gap-2">
                  {data.statuses.map((s) => (
                    <Button
                      key={s}
                      onClick={() => update(s)}
                      disabled={busy || s === data.wo.status}
                      variant={s === data.wo.status ? "default" : "outline"}
                      data-testid={`scan-set-${s}`}
                      className={`h-11 text-sm capitalize ${s === data.wo.status ? "bg-primary" : ""}`}
                    >
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (s === data.wo.status ? <><CheckCircle2 className="h-4 w-4 mr-1.5"/>{s}</> : s)}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {(data.wo.scan_history || []).length > 0 && (
              <Card className="shadow-none mt-4">
                <CardHeader className="pb-2"><CardTitle className="text-sm font-display">Scan history</CardTitle></CardHeader>
                <CardContent className="pt-0 space-y-1 text-xs">
                  {(data.wo.scan_history || []).slice(-8).reverse().map((h, i) => (
                    <div key={i} className="flex justify-between border-b py-1.5">
                      <span className="capitalize font-medium">{h.status}</span>
                      <span className="text-muted-foreground font-mono">{new Date(h.at).toLocaleString("en-IN")}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
      <Toaster position="top-center" richColors />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between text-sm border-b border-border/60 py-1.5 last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right">{value}</span>
    </div>
  );
}
